import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import {
  collection, collectionData, doc, docData,
  query, where, orderBy, limit, startAfter,
  onSnapshot, addDoc, setDoc, updateDoc, getDoc, getDocs,
  writeBatch, runTransaction, serverTimestamp, increment, arrayUnion,
  Timestamp, DocumentSnapshot, QueryDocumentSnapshot
} from '@angular/fire/firestore';
import { Observable, EMPTY, of, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FirestoreConversation } from './firebase-batch.service';

export interface FirestoreMessage {
  id: string;
  conversationId: string;
  sender: string;
  text: string;
  createdAt: Timestamp;
  isActive: boolean;
  isEdited?: boolean;
  editedAt?: Timestamp;
  editHistory?: Array<{ text: string; editedAt: Timestamp }>;
}

export interface SendMessageParams {
  conversationId: string;
  sender: string;
  text: string;
  conversationExists: boolean;
  participantIds: string[];
  schoolId: string;
  type: FirestoreConversation['type'];
  studentId?: string;
  classroomId?: string | null;
  studentStatus?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FirestoreChatService {
  private firestore = inject(Firestore);
  private lastInboxDoc: DocumentSnapshot | null = null;
  private firstMessageDoc = new Map<string, DocumentSnapshot>();

  getConversations(participantKey: string): Observable<FirestoreConversation[]> {
    const q = query(
      collection(this.firestore, 'conversations'),
      where('participantIds', 'array-contains', participantKey),
      // where('isActive', '==', true),
      orderBy('lastMessageAt', 'desc'),
      // limit(20)
    );

    return new Observable<FirestoreConversation[]>(observer => {
      const unsub = onSnapshot(q,
        snap => {
          // Only set cursor on the very first snapshot — subsequent real-time fires must
          // not overwrite a cursor that loadMoreInbox() has already advanced forward.
          if (snap.docs.length > 0 && !this.lastInboxDoc) {
            this.lastInboxDoc = snap.docs[snap.docs.length - 1];
          }
          observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreConversation)));
        },
        err => observer.error(err)
      );
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] getConversations', err);
        return EMPTY;
      })
    );
  }

  resetInboxCursor(): void {
    this.lastInboxDoc = null;
  }

  loadMoreInbox(participantKey: string): Observable<FirestoreConversation[]> {
    if (!this.lastInboxDoc) return of([]);

    const q = query(
      collection(this.firestore, 'conversations'),
      where('participantIds', 'array-contains', participantKey),
      // where('isActive', '==', true),
      orderBy('lastMessageAt', 'desc'),
      // limit(20),
      startAfter(this.lastInboxDoc)
    );

    return from(getDocs(q)).pipe(
      map(snap => {
        if (snap.docs.length > 0) {
          this.lastInboxDoc = snap.docs[snap.docs.length - 1];
        }
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreConversation));
      }),
      catchError(err => {
        console.error('[FirestoreChat] loadMoreInbox', err);
        return EMPTY;
      })
    );
  }

  getStudentConversations(participantKey: string, studentId: string): Observable<FirestoreConversation[]> {
    const q = query(
      collection(this.firestore, 'conversations'),
      where('participantIds', 'array-contains', participantKey),
      where('studentId', '==', studentId),
      // where('isActive', '==', true),
      orderBy('lastMessageAt', 'desc')
    );

    return new Observable<FirestoreConversation[]>(observer => {
      const unsub = onSnapshot(q,
        snap => observer.next(snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreConversation))),
        err => observer.error(err)
      );
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] getStudentConversations', err);
        return EMPTY;
      })
    );
  }

  async getStudentConversationsOnce(participantKey: string, studentId: string): Promise<FirestoreConversation[]> {
    const q = query(
      collection(this.firestore, 'conversations'),
      where('participantIds', 'array-contains', participantKey),
      where('studentId', '==', studentId),
      // where('isActive', '==', true),
      orderBy('lastMessageAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreConversation));
  }

  async getDMConversationOnce(participantKey: string, otherStaffId: string): Promise<FirestoreConversation | null> {
    const currentId = participantKey.split('_')[1];
    const [small, large] = [currentId, otherStaffId].sort();
    const convId = `dm-${small}-${large}`;
    const ref = doc(this.firestore, 'conversations', convId);
    const snap = await getDoc(ref);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as FirestoreConversation) : null;
  }

  watchConversation(conversationId: string): Observable<FirestoreConversation> {
    const ref = doc(this.firestore, 'conversations', conversationId);
    return new Observable<FirestoreConversation>(observer => {
      const unsub = onSnapshot(ref,
        snap => {
          if (snap.exists()) observer.next({ id: snap.id, ...snap.data() } as FirestoreConversation);
        },
        err => observer.error(err)
      );
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] watchConversation', err);
        return EMPTY;
      })
    );
  }

  getMessages(conversationId: string): Observable<FirestoreMessage[]> {
    const q = query(
      collection(this.firestore, 'messages'),
      where('conversationId', '==', conversationId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    return new Observable<FirestoreMessage[]>(observer => {
      const unsub = onSnapshot(q,
        snap => {
          const docs = snap.docs;
          if (docs.length > 0) {
            this.firstMessageDoc.set(conversationId, docs[docs.length - 1]); // oldest message
          }
          observer.next(docs.map(d => ({ id: d.id, ...d.data() } as FirestoreMessage)));
        },
        err => observer.error(err)
      );
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] getMessages', err);
        return EMPTY;
      })
    );
  }

  loadOlderMessages(conversationId: string): Observable<FirestoreMessage[]> {
    const cursor = this.firstMessageDoc.get(conversationId);
    if (!cursor) return of([]);

    // Guard against uncommitted server timestamps (happens right after first message send on a new conversation)
    if (cursor.metadata?.hasPendingWrites) return of([]);

    try {
      const q = query(
        collection(this.firestore, 'messages'),
        where('conversationId', '==', conversationId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(20),
        startAfter(cursor)
      );

      return new Observable<FirestoreMessage[]>(observer => {
        const unsub = onSnapshot(q,
          snap => {
            if (snap.docs.length > 0) {
              // Advance cursor to the oldest doc in this batch so next load-more goes further back
              this.firstMessageDoc.set(conversationId, snap.docs[snap.docs.length - 1]);
            }
            const messages = snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreMessage)).reverse();
            observer.next(messages);
          },
          err => observer.error(err)
        );
        return () => unsub();
      }).pipe(
        catchError(err => {
          console.error('[FirestoreChat] loadOlderMessages', err);
          return EMPTY;
        })
      );
    } catch {
      return of([]);
    }
  }

  async sendMessage(params: SendMessageParams): Promise<void> {
    const batch = writeBatch(this.firestore);
    const now = serverTimestamp();

    // Step 1: create conversation doc if it does not exist yet
    if (!params.conversationExists) {
      const convRef = doc(this.firestore, 'conversations', params.conversationId);
      const metaInit: Record<string, any> = {};
      params.participantIds.forEach(key => {
        metaInit[key] = { unreadCount: 0, lastReadAt: now, isActive: true, addedAt: now };
      });
      batch.set(convRef, {
        type: params.type,
        schoolId: params.schoolId,
        studentId: params.studentId ?? null,
        participantIds: params.participantIds,
        participantsMeta: metaInit,
        lastMessage: null,
        lastMessageAt: now,
        lastMessageSender: null,
        lastMessageId: null,
        ...(params.type !== 'directMessage' && params.type !== 'roomChat' && {
          studentStatus: params.studentStatus,
          classroomId: params.classroomId ?? null,
        }),

        isActive: true,
        createdAt: now,
        v: 1,
      });
    }

    // Step 2: add message document
    const msgRef = doc(collection(this.firestore, 'messages'));
    batch.set(msgRef, {
      conversationId: params.conversationId,
      sender: params.sender,
      text: params.text,
      createdAt: now,
      isActive: true,
    });

    // Step 3: update conversation lastMessage fields
    const convRef = doc(this.firestore, 'conversations', params.conversationId);
    batch.update(convRef, {
      lastMessage: params.text.substring(0, 80),
      lastMessageAt: now,
      lastMessageSender: params.sender,
      lastMessageId: msgRef.id,
    });

    // Step 3.5: For existing conversations, ensure sender is stored in participantIds (handles legacy data)
    if (params.conversationExists) {
      batch.update(convRef, {
        participantIds: arrayUnion(params.sender)
      });
    }

    // Step 4: increment unreadCount for every recipient (everyone except sender)
    const recipients = params.participantIds.filter(k => k !== params.sender);
    recipients.forEach(recipientKey => {
      batch.update(convRef, {
        [`participantsMeta.${recipientKey}.unreadCount`]: increment(1),
      });
    });

    // Step 5: increment users.totalUnread for every recipient
    recipients.forEach(recipientKey => {
      const unreadRef = doc(this.firestore, 'users', recipientKey);
      batch.set(unreadRef, { totalUnread: increment(1) }, { merge: true });
    });

    try {
      await batch.commit();
    } catch (err: any) {
      if (err.code === 'permission-denied') {
        console.error('Permission denied sending message');
      } else {
        console.error('Message failed to send:', err);
      }
      throw err;
    }
  }

  buildSendBatch(params: SendMessageParams): any {
    const batch = writeBatch(this.firestore);
    const now = serverTimestamp();

    // Step 1: create conversation doc if it does not exist yet
    if (!params.conversationExists) {
      const convRef = doc(this.firestore, 'conversations', params.conversationId);
      const metaInit: Record<string, any> = {};
      params.participantIds.forEach(key => {
        metaInit[key] = { unreadCount: 0, lastReadAt: now, isActive: true, addedAt: now };
      });
      batch.set(convRef, {
        type: params.type,
        schoolId: params.schoolId,
        studentId: params.studentId ?? null,
        participantIds: params.participantIds,
        participantsMeta: metaInit,
        lastMessage: null,
        lastMessageAt: now,
        lastMessageSender: null,
        lastMessageId: null,
        ...(params.type !== 'directMessage' && params.type !== 'roomChat' && {
          studentStatus: params.studentStatus,
          classroomId: params.classroomId ?? null,
        }),

        isActive: true,
        createdAt: now,
        v: 1,
      });
    }

    // Step 2: add message document
    const msgRef = doc(collection(this.firestore, 'messages'));
    batch.set(msgRef, {
      conversationId: params.conversationId,
      sender: params.sender,
      text: params.text,
      createdAt: now,
      isActive: true,
    });

    // Step 3: update conversation lastMessage fields
    const convRef = doc(this.firestore, 'conversations', params.conversationId);
    batch.update(convRef, {
      lastMessage: params.text.substring(0, 80),
      lastMessageAt: now,
      lastMessageSender: params.sender,
      lastMessageId: msgRef.id,
    });

    // Step 3.5: For existing conversations, ensure sender is stored in participantIds (handles legacy data)
    if (params.conversationExists) {
      batch.update(convRef, {
        participantIds: arrayUnion(params.sender)
      });
    }

    // Step 4: increment unreadCount for every recipient (everyone except sender)
    const recipients = params.participantIds.filter(k => k !== params.sender);
    recipients.forEach(recipientKey => {
      batch.update(convRef, {
        [`participantsMeta.${recipientKey}.unreadCount`]: increment(1),
      });
    });

    // Step 5: increment users.totalUnread for every recipient
    recipients.forEach(recipientKey => {
      const unreadRef = doc(this.firestore, 'users', recipientKey);
      batch.set(unreadRef, { totalUnread: increment(1) }, { merge: true });
    });

    return batch;
  }

  async markAsRead(conversationId: string, participantKey: string): Promise<void> {
    await runTransaction(this.firestore, async tx => {
      const convRef = doc(this.firestore, 'conversations', conversationId);
      const convSnap = await tx.get(convRef);
      if (!convSnap.exists()) return;

      const meta = convSnap.data()?.['participantsMeta']?.[participantKey];
      const unread = meta?.unreadCount ?? 0;
      if (unread === 0) return;

      const now = serverTimestamp();
      tx.update(convRef, {
        [`participantsMeta.${participantKey}.unreadCount`]: 0,
        [`participantsMeta.${participantKey}.lastReadAt`]: now,
      });

      const unreadRef = doc(this.firestore, 'users', participantKey);
      tx.set(unreadRef, { totalUnread: increment(-unread) }, { merge: true });
    }).catch(err => {
      console.error('[FirestoreChat] markAsRead', err);
      throw err;
    });
  }

  watchUserUnread(participantKey: string): Observable<number> {
    const ref = doc(this.firestore, 'users', participantKey);
    return new Observable<number>(observer => {
      const unsub = onSnapshot(ref,
        snap => observer.next(snap.exists() ? (snap.data()?.['totalUnread'] ?? 0) : 0),
        err => observer.error(err)
      );
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] watchUserUnread', err);
        return EMPTY;
      })
    );
  }


  watchActiveStudentUnread(studentId: string, guardianKey: string): Observable<number> {
    const q = query(
      collection(this.firestore, 'conversations'),
      where('studentId', '==', studentId),
      where('participantIds', 'array-contains', guardianKey)
    );

    return new Observable<number>(observer => {
      const unsub = onSnapshot(q, snap => {
        let total = 0;
        snap.docs.forEach(doc => {
          const meta = doc.data()['participantsMeta']?.[guardianKey];
          if (meta?.unreadCount) {
            total += meta.unreadCount;
          }
        });
        observer.next(total);
      }, err => observer.error(err));
      return () => unsub();
    }).pipe(
      catchError(err => {
        console.error('[FirestoreChat] watchActiveStudentUnread', err);
        return EMPTY;
      })
    );
  }

  async editMessage(messageId: string, newText: string, previousText: string): Promise<void> {
    const ref = doc(this.firestore, 'messages', messageId);
    try {
      await updateDoc(ref, {
        text: newText,
        isEdited: true,
        editedAt: serverTimestamp(),
        editHistory: arrayUnion({ text: previousText, editedAt: Timestamp.now() })
      });
    } catch (err) {
      console.error('[FirestoreChat] editMessage', err);
      throw err;
    }
  }

  async deleteMessage(messageId: string): Promise<void> {
    const msgRef = doc(this.firestore, 'messages', messageId);
    try {
      // 1. Read message data — needed to know conversationId, sender, createdAt
      const msgSnap = await getDoc(msgRef);
      if (!msgSnap.exists()) return;
      const msgData: any = msgSnap.data();
      const convId: string | null = msgData.conversationId ?? null;

      if (!convId) {
        await updateDoc(msgRef, { isActive: false });
        return;
      }

      const senderKey: string = msgData.sender ?? '';
      const msgCreatedAtMs: number | null = msgData.createdAt?.toMillis ? msgData.createdAt.toMillis() : null;
      const convRef = doc(this.firestore, 'conversations', convId);

      // 2. Pre-read conversation only for wasLast check — needed because the replacement
      //    query (step 3) is a Firestore collection query that cannot run inside a transaction.
      const preConvSnap = await getDoc(convRef);
      const preConvData: any = preConvSnap.exists() ? preConvSnap.data() : {};
      const lastSender: string | null = preConvData.lastMessageSender ?? null;
      const convLastAtMs: number | null = preConvData.lastMessageAt?.toMillis ? preConvData.lastMessageAt.toMillis() : null;
      const wasLast = lastSender === senderKey &&
        convLastAtMs !== null && msgCreatedAtMs !== null &&
        convLastAtMs === msgCreatedAtMs;

      // 3. If last message, find replacement while this message is still active
      //    (filter out the target so we get the truly previous message)
      let replacementMsg: any = null;
      if (wasLast) {
        const snap = await getDocs(query(
          collection(this.firestore, 'messages'),
          where('conversationId', '==', convId),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          limit(2)
        ));
        const others = snap.docs.filter(d => d.id !== messageId);
        if (others.length > 0) {
          replacementMsg = { id: others[0].id, ...others[0].data() };
        }
      }

      // 4. Single atomic transaction.
      //    Firestore rule: ALL tx.get() reads must come before ANY tx.update()/tx.set() write.
      //    Structure: read everything → compute → write everything.
      await runTransaction(this.firestore, async tx => {

        // ── PHASE 1: reads ────────────────────────────────────────────────────
        const freshMsgSnap = await tx.get(msgRef);
        const freshConvSnap = await tx.get(convRef);

        // ── PHASE 2: compute (pure logic, no I/O) ────────────────────────────
        if (!freshMsgSnap.exists() || freshMsgSnap.data()?.['isActive'] === false) return;

        const convExists = freshConvSnap.exists();
        const freshMeta: Record<string, any> = convExists
          ? (freshConvSnap.data()?.['participantsMeta'] ?? {})
          : {};

        // Determine unread recipients from participantIds (the authoritative list).
        // A recipient is "unread" only when their unreadCount > 0 AND
        // their lastReadAt is before this message's createdAt.
        const freshParticipantIds: string[] = convExists
          ? (freshConvSnap.data()?.['participantIds'] ?? [])
          : [];
        const unreadRecipients: string[] = [];
        if (convExists && msgCreatedAtMs !== null) {
          for (const key of freshParticipantIds) {
            if (key === senderKey) continue;
            const m = freshMeta[key] as any;
            if (!m) continue;
            const lastReadAtMs: number | null = m.lastReadAt?.toMillis ? m.lastReadAt.toMillis() : null;
            if ((m.unreadCount ?? 0) > 0 && (lastReadAtMs === null || lastReadAtMs < msgCreatedAtMs)) {
              unreadRecipients.push(key);
            }
          }
        }

        // Merge all conversation field updates into a single object to avoid
        // multiple tx.update() calls on the same doc (cleaner and avoids ordering issues).
        const convUpdates: Record<string, any> = {};

        if (unreadRecipients.length > 0) {
          unreadRecipients.forEach(key => {
            convUpdates[`participantsMeta.${key}.unreadCount`] = increment(-1);
          });
        }

        if (wasLast) {
          if (replacementMsg) {
            const text: string = replacementMsg.text ?? '';
            convUpdates['lastMessage'] = text.length > 80 ? text.substring(0, 80) : text;
            convUpdates['lastMessageAt'] = replacementMsg.createdAt ?? null;
            convUpdates['lastMessageSender'] = replacementMsg.sender ?? null;
          } else {
            convUpdates['lastMessage'] = null;
            convUpdates['lastMessageAt'] = null;
            convUpdates['lastMessageSender'] = null;
          }
        }

        // ── PHASE 3: writes ───────────────────────────────────────────────────
        tx.update(msgRef, { isActive: false });

        if (convExists && Object.keys(convUpdates).length > 0) {
          tx.update(convRef, convUpdates);
        }

        // users/{key}.totalUnread — one set per unread recipient
        unreadRecipients.forEach(key => {
          tx.set(doc(this.firestore, 'users', key), { totalUnread: increment(-1) }, { merge: true });
        });
      });

    } catch (err) {
      console.error('[FirestoreChat] deleteMessage', err);
      throw err;
    }
  }

  async getConversationOnce(conversationId: string): Promise<FirestoreConversation | null> {
    try {
      const snap = await getDoc(doc(this.firestore, 'conversations', conversationId));
      return snap.exists() ? ({ id: snap.id, ...snap.data() } as FirestoreConversation) : null;
    } catch (err) {
      console.error('[FirestoreChat] getConversationOnce', err);
      return null;
    }
  }

  async conversationExists(conversationId: string): Promise<boolean> {
    try {
      const snap = await getDoc(doc(this.firestore, 'conversations', conversationId));
      return snap.exists();
    } catch (err) {
      console.error('[FirestoreChat] conversationExists', err);
      return false;
    }
  }

  async getConversationsOnce(participantKey: string, limitCount: number = 20): Promise<FirestoreConversation[]> {
    try {
      const q = query(
        collection(this.firestore, 'conversations'),
        where('participantIds', 'array-contains', participantKey),
        where('isActive', '==', true),
        orderBy('lastMessageAt', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as FirestoreConversation));
    } catch (err) {
      console.error('[FirestoreChat] getConversationsOnce', err);
      return [];
    }
  }

  async createConversation(conversation: any): Promise<void> {
    // Map Conversation to FirestoreConversation
    const firestoreConv: Omit<FirestoreConversation, 'id'> = {
      type: conversation.type === 'GROUP' ? 'studentGroup' : conversation.type === 'PRIVATE_PARENT' ? 'studentPrivate' : conversation.type === 'ADMIN_CHAT' ? 'adminGroup' : conversation.type === 'ADMIN_PRIVATE' ? 'adminPrivate' : 'directMessage',
      studentId: conversation.studentId,
      schoolId: conversation.schoolId,
      participantIds: Object.keys(conversation.participants),
      participantsMeta: Object.keys(conversation.participants).reduce((meta, key) => {
        meta[key] = { unreadCount: 0, lastReadAt: serverTimestamp(), isActive: true, addedAt: serverTimestamp() };
        return meta;
      }, {} as any),
      lastMessage: conversation.lastMessage,
      lastMessageAt: serverTimestamp(),
      lastMessageSender: null,
      lastMessageId: null,
      ...(conversation.type !== 'STAFF_DIRECT' && {
        studentStatus: conversation.studentStatus,
        classroomId: conversation.classroomId ?? null,
      }),
      isActive: conversation.isActive,
      createdAt: serverTimestamp(),
      v: 1
    };

    try {
      await setDoc(doc(this.firestore, 'conversations', conversation.id), firestoreConv);
    } catch (err) {
      console.error('[FirestoreChat] createConversation', err);
      throw err;
    }
  }
}